import { ThemeEvents } from '@theme/events';

// Module-level cache: survives morph clobbering of element contents and
// custom-element instance recreation. Keyed by `${url}|${productId}`.
// Empty string cached value means "API returned no recommendations" — used
// to short-circuit refetch on subsequent morph cycles for the same product.
/** @type {Map<string, string>} */
const recommendationCache = new Map();

class CdCartRecommendations extends HTMLElement {
  static observedAttributes = ['data-product-id'];

  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** Suppresses MutationObserver while we set innerHTML ourselves */
  #suppressMutation = false;

  /** Tracks whether intersection has fired (i.e. drawer was opened at least once) */
  #hasBeenVisible = false;

  connectedCallback() {
    // Watch for the section morph clobbering our fetched contents back to
    // the skeleton placeholder. When detected, restore from cache.
    this.#mutationObserver = new MutationObserver(() => {
      if (this.#suppressMutation) return;
      // Defer so morph finishes its child updates before we restore.
      queueMicrotask(() => this.#restoreFromCacheIfPossible());
    });
    this.#mutationObserver.observe(this, { childList: true });

    // Lazy-load on first visibility (drawer opened) to avoid network hits per page load.
    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#intersectionObserver = null;
        this.#hasBeenVisible = true;
        this.#updateForCurrentProduct();
      },
      { rootMargin: '0px 0px 200px 0px' }
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#fetchController?.abort();
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-product-id') return;
    if (oldValue === newValue || !newValue) return;
    // Morph after a cart update changed our source product. Defer so morph
    // finishes its child updates first, then react.
    queueMicrotask(() => {
      if (this.#hasBeenVisible) this.#updateForCurrentProduct();
    });
  }

  #cacheKey(productId = this.dataset.productId) {
    return `${this.dataset.url || ''}|${productId || ''}`;
  }

  /**
   * Apply cached recommendations for the current product if available.
   * Handles three cases:
   *   - cache miss: returns false
   *   - cached HTML: applies it, returns true
   *   - cached empty (API returned nothing): hides element, returns true
   */
  #restoreFromCacheIfPossible() {
    const productId = this.dataset.productId;
    if (!productId) return false;

    const key = this.#cacheKey(productId);
    if (!recommendationCache.has(key)) return false;

    const cached = recommendationCache.get(key) || '';

    if (cached === '') {
      this.#setContent('');
      this.setAttribute('hidden', '');
      this.dataset.cachedFor = productId;
      return true;
    }

    // Skip rewrite if already showing this cached content
    if (this.dataset.cachedFor === productId && this.innerHTML.includes('cd-cart-recommendations__list')) {
      this.removeAttribute('hidden');
      return true;
    }

    this.#setContent(cached);
    this.dataset.cachedFor = productId;
    this.removeAttribute('hidden');
    return true;
  }

  #updateForCurrentProduct() {
    const productId = this.dataset.productId;
    if (!productId) return;

    if (this.#restoreFromCacheIfPossible()) return;

    this.#fetch(productId);
  }

  /**
   * @param {string} productId
   */
  async #fetch(productId) {
    const baseUrl = this.dataset.url;
    if (!baseUrl) return;

    const url = `${baseUrl}&product_id=${encodeURIComponent(productId)}`;

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#fetchController.signal });
      if (!response.ok) {
        // Don't cache HTTP errors — let the next cart update retry.
        this.setAttribute('hidden', '');
        return;
      }

      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const sourceEl = parsed.querySelector(`#${CSS.escape(this.id)}`);
      const html = sourceEl?.innerHTML?.trim() || '';

      // Cache both populated and empty results. Caching empty prevents repeated
      // fetches for products with no recommendations as the cart morphs.
      recommendationCache.set(this.#cacheKey(productId), html);

      // Product may have changed again while this fetch was in flight —
      // only apply if it's still current.
      if (this.dataset.productId !== productId) return;

      if (!html) {
        this.#setContent('');
        this.setAttribute('hidden', '');
        this.dataset.cachedFor = productId;
        return;
      }

      this.#setContent(html);
      this.dataset.cachedFor = productId;
      this.removeAttribute('hidden');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('cd-cart-recommendations fetch failed:', error);
      this.setAttribute('hidden', '');
    }
  }

  /**
   * @param {string} html
   */
  #setContent(html) {
    this.#suppressMutation = true;
    this.innerHTML = html;
    queueMicrotask(() => {
      this.#suppressMutation = false;
    });
  }
}

// Register document-level listeners + define() exactly once. Module re-evaluation
// (Section Rendering API morphs, theme editor reloads) would otherwise stack
// duplicate handlers on `document` — the define() guard protects only the
// custom-element registration, not these listeners.
if (!customElements.get('cd-cart-recommendations')) {
  // Force re-render on cart updates: the morph after a cart change updates our
  // `data-product-id` attribute, which our attributeChangedCallback hooks into.
  // As a backup (e.g. CartAddEvent with empty resource), nudge the attribute
  // directly when the new first cart item differs from our current product.
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const newFirstProductId = event?.detail?.resource?.items?.[0]?.product_id;
    if (!newFirstProductId) return;

    const el = document.querySelector('cd-cart-recommendations');
    if (!el) return;

    const current = el.dataset.productId;
    if (String(newFirstProductId) === current) return;

    el.dataset.productId = String(newFirstProductId);
  });

  // Live price updates when the variant select inside a recommendation card changes.
  // The select is the form's `name="id"` field, so the new variant is what gets
  // added on submit. We just keep the displayed price in sync.
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    if (!select.matches('.cd-cart-rec__variant-select')) return;

    const card = select.closest('.cd-cart-rec');
    if (!card) return;

    const option = select.options[select.selectedIndex];
    if (!option) return;

    const current = card.querySelector('.cd-cart-rec__price-current');
    const compare = card.querySelector('.cd-cart-rec__price-compare');

    if (current && option.dataset.priceFormatted) {
      current.textContent = option.dataset.priceFormatted;
    }
    if (compare) {
      if (option.dataset.compareFormatted) {
        compare.textContent = option.dataset.compareFormatted;
        compare.removeAttribute('hidden');
      } else {
        compare.setAttribute('hidden', '');
      }
    }
  });

  customElements.define('cd-cart-recommendations', CdCartRecommendations);
}
